/* global btoa */
import appConfig from 'app/config/client';
import { DEFAULT_SCOPES } from 'app/constants/enums/Ability';
import { DAILY_NEWS_COURSE_ID } from 'app/constants/Courses';
import { versionFileName } from 'config/shared';
import { PUBLISHED } from 'app/constants/LessonTypes';
import { Action } from 'app/services/oauth/constants';
import { getBackendPath } from 'app/utils/paths';
import { GET_PARAMS, DELETE_PARAMS, POST_PARAMS, PUT_PARAMS, cacheBuster } from 'app/utils/api/next';
import { CREATED_AT_DESCENDING } from 'app/constants/EditorSortOrders';

export { cacheBuster, getPresence } from 'app/utils/api/next';

const { basePath } = appConfig.environment;

// Authentication

const oauthClientAuthentication = btoa(
  `${appConfig.oauth.clientId}:${appConfig.oauth.clientSecret}`,
);

export const postOauthToken = (username, password, scopes) => ({
  ...POST_PARAMS,
  requestHeaders: {
    ...POST_PARAMS.requestHeaders,
    Authorization: `Basic ${oauthClientAuthentication}`,
  },
  data: {
    scope:         (scopes || DEFAULT_SCOPES).toJSON().join(' '),
    grant_type:    'password',
    username,
    password,
  },
  url:  `${getBackendPath()}/api/oauth/token`,
});

export const postRefreshOauthToken = ({ refresh_token }) => ({
  ...POST_PARAMS,
  requestHeaders: {
    ...POST_PARAMS.requestHeaders,
    Authorization: `Basic ${oauthClientAuthentication}`,
  },
  data: {
    grant_type:    'refresh_token',
    refresh_token,
  },
  url:  `${getBackendPath()}/api/oauth/token`,
});

export const postOauthRevoke = token => ({
  ...POST_PARAMS,
  requestHeaders: {
    ...POST_PARAMS.requestHeaders,
    Authorization: `Basic ${oauthClientAuthentication}`,
  },
  url: `${getBackendPath()}/api/oauth/revoke`,
  data: {
    token,
  },
});

/**
 * @type {(request: IDelegateRequest) => object}
 */
export const delegateOauth = ({ response_type, redirect_uri, scope, client_id }) => ({
  ...POST_PARAMS,
  url:  `${getBackendPath()}/api/oauth/token`,
  data: {
    grant_type: 'delegate_credentials',
    response_type,
    redirect_uri,
    scope,
    client_id,
  },
});

export const signup = (data, recaptchaToken, signupStateParam) => ({
  ...POST_PARAMS,
  url: `${getBackendPath()}/api/users/signup?recaptcha_token=${encodeURIComponent(recaptchaToken)}`
    + `${(signupStateParam ? `&state=${JSON.stringify(signupStateParam)}` : '')}`,
  data,
});

export const getReamazeAuthKey = () => ({
  ...GET_PARAMS,
  url: `${getBackendPath()}/api/users/current/reamaze_token`,
});

export const cancelEmailChange = (id) => ({
  ...POST_PARAMS,
  url: `${getBackendPath()}/api/users/${id}/cancel_email_change`,
});

export const cancelCurrentUserEmailChange = () => ({
  ...POST_PARAMS,
  url: `${getBackendPath()}/api/users/current/cancel_email_change`,
});

export const clearUserSession = (id) => ({
  ...POST_PARAMS,
  url: `${getBackendPath()}/api/users/${id}/clear_sessions`,
});

// Users (that don't use view models)

export const postForgotPassword = email => ({
  ...POST_PARAMS,
  url: `${getBackendPath()}/api/users/forgot_password`,
  data: { email },
});

export const postResetPassword = ({ token, password }) => ({
  ...POST_PARAMS,
  url: `${getBackendPath()}/api/users/reset_password`,
  data: { token, password },
});

export const postSendConfirmation = (email) => ({
  ...POST_PARAMS,
  url:  `${getBackendPath()}/api/users/send_confirmation`,
  data: { email },
});

export const postConfirm = (token) => ({
  ...POST_PARAMS,
  url:  `${getBackendPath()}/api/users/confirm`,
  data: { token },
});

export const postSendUnlock = (email) => ({
  ...POST_PARAMS,
  url:  `${getBackendPath()}/api/users/send_unlock`,
  data: { email },
});

export const postUnlock = (token) => ({
  ...POST_PARAMS,
  url: `${getBackendPath()}/api/users/unlock`,
  data: { token },
});

/**
 * @return {{method: 'GET', url: string,
 *           data: {action: Action.ATTACH, state: string, bearer_token: string, brand: string}}}
 */
export const attachExternalOauth = ({ provider, state, bearer_token, brand, dmm_env }) => ({
  ...GET_PARAMS,
  url:  `${getBackendPath()}/auth/${provider}`,
  data: {
    action: Action.ATTACH,
    state,
    bearer_token,
    brand,
    dmm_env,
  },
});

// Entity fundamentals

const getEntity = (urlPart) => (data) => ({
  ...GET_PARAMS,
  url: `${getBackendPath()}/api/${urlPart}`,
  data,
});

const searchEntity = (urlPart) => (data) => ({
  ...GET_PARAMS,
  url: `${getBackendPath()}/api/${urlPart}/search`,
  data,
});

const getEntityById = (urlPart) => (id) => ({
  ...GET_PARAMS,
  url: `${getBackendPath()}/api/${urlPart}/${id}`,
});

const postEntities = (urlPart) => (data) => ({
  ...POST_PARAMS,
  data,
  url: `${getBackendPath()}/api/${urlPart}`,
});

const deleteEntity = (urlPart) => (id) => ({
  ...DELETE_PARAMS,
  url: `${getBackendPath()}/api/${urlPart}/${id}`,
});

const entityRoutes = (urlPart) => ({
  get:     getEntity(urlPart),
  getById: getEntityById(urlPart),
  search:  searchEntity(urlPart),
  post:    postEntities(urlPart),
  delete:  deleteEntity(urlPart),
});

const getExternalAssociation = (type, associationType) => (id, data) => ({
  ...GET_PARAMS,
  url: `${getBackendPath()}/api/${type}/${id}/${associationType}`,
  data,
});

const searchExternalAssociation = (type, associationType) => (id, data) => ({
  ...GET_PARAMS,
  url: `${getBackendPath()}/api/${type}/${id}/${associationType}/search`,
  data,
});

const putExternalAssociation = (type, associationType) => (id, data) => ({
  ...PUT_PARAMS,
  data,
  url: `${getBackendPath()}/api/${type}/${id}/${associationType}`,
});

const postExternalAssociation = (type, associationType) => (id, data) => ({
  ...POST_PARAMS,
  data,
  url: `${getBackendPath()}/api/${type}/${id}/${associationType}`,
});

const externalAssociationRoutes = (type, associationType) => ({
  get:    getExternalAssociation(type, associationType),
  search: searchExternalAssociation(type, associationType),
  post:   postExternalAssociation(type, associationType),
  put:    putExternalAssociation(type, associationType),
});

// Entities

export const bannerRoutes = entityRoutes('banners');
export const {
  get: getBanners,
  getById: getBanner,
  search: searchBanners,
  post: postBanners,
  delete: deleteBanner,
} = bannerRoutes;

export const serviceDomainRoutes = entityRoutes('service_domains');
export const { get: getServiceDomain } = serviceDomainRoutes;

export const organizationRoutes = entityRoutes('organizations');
export const organizationDetailsRoutes = entityRoutes('organization_details');
export const brandRoutes = entityRoutes('brands');
export const curriculumRoutes = entityRoutes('curricula');
export const {
  get: getCurricula,
  // we don't use the other endpoints just yet
} = curriculumRoutes;

export const curriculumCategoryRoutes = externalAssociationRoutes('curricula', 'categories');

export const categoryRoutes = entityRoutes('categories');
export const {
  get: getCategories,
  getById: getCategory,
  search: searchCategories,
  post: postCategories,
  delete: deleteCategory,
} = categoryRoutes;

export const categoryCourseRoutes = externalAssociationRoutes('categories', 'courses');
export const {
  get: getCategoryCourses,
  search: searchCategoryCourses,
  post: postCategoryCourses,
  put: putCategoryCourses,
} = categoryCourseRoutes;

export const courseRoutes = entityRoutes('courses');
export const {
  get: getCourses,
  getById: getCourse,
  search: searchCourses,
  post: postCourses,
  delete: deleteCourse,
} = courseRoutes;

export const courseLessonRoutes = externalAssociationRoutes('courses', 'lessons');
export const {
  put: putCourseLessons,
} = courseLessonRoutes;

export const courseLessonHeaderRoutes = externalAssociationRoutes('courses', 'lesson_headers');
export const {
  get: getCourseLessonHeaders,
  search: searchCourseLessonHeaders,
  post: postCourseLessonHeaders,
  put: putCourseLessonHeaders,
} = courseLessonHeaderRoutes;

export const getCurrentPublishedCourseLessonHeaders = (id, data) =>
  getCourseLessonHeaders(id, {
    ...data,
    type: PUBLISHED.id,
    published_latest: true,
  });

export const deleteLessonFromCourse = (courseId, lessonId) => ({
  ...DELETE_PARAMS,
  url: `${getBackendPath()}/api/courses/${courseId}/lessons/${lessonId}`,
});

export const commonTextRoutes = entityRoutes('common_texts');
export const {
               get: getCommonTexts,
               getById: getCommonText,
               search: searchCommonText,
               post: postCommonTexts,
               delete: deleteCommonText,
             } = commonTextRoutes;

export const lessonRoutes = entityRoutes('lessons');
export const {
               get: getLessons,
               getById: getLesson,
               search: searchLessons,
               post: postLessons,
               delete: deleteLesson,
             } = lessonRoutes;

export const lessonHeaderRoutes = entityRoutes('lesson_headers');
export const getCurrentLessonHeader = (lessonId) => ({
  ...GET_PARAMS,
  url: `${getBackendPath()}/api/lesson_headers/${lessonId}/current`,
});

export const {
               get: getLessonHeaders,
               search: searchLessonHeaders,
             } = lessonHeaderRoutes;

export const duplicateLesson = (lessonId) => ({
  ...POST_PARAMS,
  url: `${getBackendPath()}/api/lessons/${lessonId}/duplicate`,
});

export const hideLesson = (id) => ({
  ...POST_PARAMS,
  url: `${getBackendPath()}/api/lessons/${id}/hide`,
});

export const unhideLesson = (id) => ({
  ...POST_PARAMS,
  url: `${getBackendPath()}/api/lessons/${id}/unhide`,
});

export const dialogSpeakerRoutes = entityRoutes('dialog_speakers');
export const {
  get: getDialogSpeakers,
  getById: getDialogSpeaker,
  search: searchDialogSpeakers,
  post: postDialogSpeakers,
  delete: deleteDialogSpeaker,
} = dialogSpeakerRoutes;

/**
 * Gets the latest published daily news lessons. Includes pagination parameters, and parameters for
 * filtering by tag and content level.
 */
export const getDailyNewsLessons = data =>
  getCurrentPublishedCourseLessonHeaders(DAILY_NEWS_COURSE_ID, data);

/**
 * Gets the latest published daily news lessons, but only returns the first `count` lessons for each
 * tag in `tags`. Used on the daily news timeline to only fetch the first 5 lessons for each daily
 * news category.
 */
export const getDailyNewsLessonsByTag = ({ tags, count, min_level, max_level, allow_license_partners }) => ({
  ...GET_PARAMS,
  url: `${getBackendPath()}/api/courses/${DAILY_NEWS_COURSE_ID}/lesson_headers/by_tag`,
  data: {
    tags,
    count,
    min_level,
    max_level,
    type: PUBLISHED.id,
    published_latest: true,
    allow_license_partners,
  },
});

export const getRelatedDailyNewsLessons = ({ id, page_size, order, allow_license_partners }) => ({
  ...GET_PARAMS,
  url: `${getBackendPath()}/api/lesson_headers/${id}/related`,
  data: {
    page_size,
    order,
    allow_license_partners,
  },
});

export const getLessonCourses = (id, data) => ({
  ...GET_PARAMS,
  url: `${getBackendPath()}/api/lessons/${id}/courses`,
  data,
});

// Users

export const userRoutes = entityRoutes('users');
export const {
  get: getUsers,
  getById: getUser,
  post: postUsers,
  delete: deleteUser,
} = userRoutes;

export const blockedEmailDomainRoutes = entityRoutes('blocked_email_domains');

export const wordRoutes = entityRoutes('words');
export const getWordLessonHeaders = (wordId, published_latest) => {
  const data = published_latest === undefined ? {} : {
    published_latest,
  };
  return {
    ...GET_PARAMS,
    url: `${getBackendPath()}/api/words/${wordId}/lesson_headers`,
    data,
  };
};

export const postWords = wordRoutes.post;
export const sentenceRoutes = entityRoutes('sentences');
export const wordSentenceRoutes = entityRoutes('word_sentences');
export const postPromoteWord = (wordId) => ({
  ...POST_PARAMS,
  url: `${getBackendPath()}/api/words/${wordId}/promote`,
});
export const postPromoteSentenceFromLesson = (vocabSectionWordSentenceId) => ({
  ...POST_PARAMS,
  url: `${getBackendPath()}/api/word_sentences/promote_from_lesson`,
  data: {
    id: vocabSectionWordSentenceId,
  },
});
export const postPromoteSentence = (localSentenceId) => ({
  ...POST_PARAMS,
  url: `${getBackendPath()}/api/sentences/${localSentenceId}/promote`,
});

export const wordSentencesAssociationRoutes = externalAssociationRoutes('words', 'word_sentences');

// Question/Answer routes
export const usageQuestionsRoutes = entityRoutes('usage_questions');
export const wordUsageQuestionAssociationRoutes = externalAssociationRoutes('words', 'usage_questions');
export const uploadUsageQuestions = (file) => ({
  ...POST_PARAMS,
  url: `${getBackendPath()}/api/usage_questions/upload`,
  data: file,
});

export const likeUsageAnswerRoute = (answerId) => ({
  ...POST_PARAMS,
  url: `${getBackendPath()}/api/usage_answers/${answerId}/like`,
});
export const unlikeUsageAnswerRoute = (answerId) => ({
  ...DELETE_PARAMS,
  url: `${getBackendPath()}/api/usage_answers/${answerId}/like`,
});

// Vocab Lists
export const vocabListRoutes = entityRoutes('vocab_lists');
export const vocabListHeaderRoutes = entityRoutes('vocab_list_headers');

export const userProfileRoutes = entityRoutes('user_profiles');

export const userDetailRoutes = entityRoutes('user_details');

export const createUser = (data) => ({
  ...POST_PARAMS,
  url: `${getBackendPath()}/api/users/bulk_signup`,
  data,
});

export const getCurrentUser = () => ({
  ...GET_PARAMS,
  url: `${getBackendPath()}/api/users/current`,
});

// Staff

export const staffMemberRoutes = entityRoutes('staff_members');

// Issues

export const postLessonReportIssue = (lessonId, data) => ({
  ...POST_PARAMS,
  url: `${getBackendPath()}/api/lessons/${lessonId}/report_issue`,
  data,
});

// Published lessons

export const getCurrentPublishedLessonHeaders = data =>
  getLessonHeaders({
    ...data,
    type: PUBLISHED.id,
    published_latest: true,
  });

export const getCurrentPublishedLesson = (id, tieupToken, lessonBookingId) => ({
  ...GET_PARAMS,
  url: `${getBackendPath()}/api/lessons/${id}/current`,
  data: {
    tieup_token: tieupToken,
    lesson_booking_id: lessonBookingId,
  },
});

// Published lessons admin

export const getPublishedLessonsForLesson = (id, data) => ({
  ...GET_PARAMS,
  url: `${getBackendPath()}/api/lessons/${id}/published_lessons`,
  data,
});

export const validateLesson = (id) => ({
  ...POST_PARAMS,
  url: `${getBackendPath()}/api/lessons/${id}/validate`,
});

export const publishLesson = (id, options) => ({
  ...POST_PARAMS,
  url:  `${getBackendPath()}/api/lessons/${id}/publish`,
  data: options,
});

export const republishLesson = (id, options) => ({
  ...POST_PARAMS,
  url:  `${getBackendPath()}/api/lessons/${id}/republish`,
  data: options,
});

// Tags

export const getTags = (data) => ({
  ...GET_PARAMS,
  url: `${getBackendPath()}/api/tags`,
  data,
});

export const getInternalTags = (data) => ({
  ...GET_PARAMS,
  url: `${getBackendPath()}/api/internal_tags`,
  data,
});

// App Status

export const getCacheCheck = () => ({
  ...GET_PARAMS,
  url: `${getBackendPath()}/api/cache_check`,
});

export const getClientVersion = () => ({
  ...GET_PARAMS,
  url: `${basePath}/${versionFileName}?${cacheBuster()}`,
});

export const getDeployInfo = () => ({
  ...GET_PARAMS,
  url: `${getBackendPath()}/api/deploy_info.json`,
});

// WebRTC

export const getRoomPersistence = (roomId, data) => ({
  ...POST_PARAMS,
  url: `${getBackendPath()}/api/rtc/rooms/${roomId}/persistence/retrieve`,
  data,
});

export const postJoinRoom = (roomID, data) => {
  return {
    ...POST_PARAMS,
    url: `${getBackendPath()}/api/rtc/rooms/${roomID}/join`,
    data,
  };
};

/**
 * @param {string} roomId
 * @param {FormData} formData  an optional participant secret, image file, and position (message_id)
 */
export const postChatImage = (roomId, formData) => ({
  // when a FormData object is being sent, the request headers are set automatically
  method: POST_PARAMS.method,
  url: `${getBackendPath()}/api/rtc/rooms/${roomId}/media`,
  data: formData,
});

export const meetingBookingRoutes = entityRoutes('meeting_bookings');
export const {
  post: postMeetingBooking,
} = meetingBookingRoutes;

export const botBookingRoutes = entityRoutes('bot_bookings');
export const {
  post: postBotBooking,
} = botBookingRoutes;

const LESSON_BOOKING_URL_PART = 'lesson_bookings';
export const lessonBookingRoutes = entityRoutes(LESSON_BOOKING_URL_PART);
export const {
  post: postLessonBooking,
} = lessonBookingRoutes;

/**
 * @param {string} id
 */
export const getCombinedLessonBooking = (id) => ({
  ...GET_PARAMS,
  url: `${getBackendPath()}/api/${LESSON_BOOKING_URL_PART}/${id}/combined`,
});

/**
 * @param {string} roomId
 * @param {RtcBotType} botType
 */
export const postLaunchBot = (roomId, botType) => ({
  ...POST_PARAMS,
  data: {
    bot_type: botType,
  },
  url: `${getBackendPath()}/api/rtc/rooms/${roomId}/launch_bot`,
});

export const teacherApplicationRoutes = entityRoutes('teacher_applications');
export const {
  get: getTeacherApplications,
  getById: getTeacherApplication,
  search: searchTeacherApplications,
  post: postTeacherApplications,
  delete: deleteTeacherApplication,
} = teacherApplicationRoutes;

export const userTeacherApplicationRoutes = externalAssociationRoutes('users', 'teacher_applications');
export const {
  put: putUserTeacherApplications,
} = userTeacherApplicationRoutes;

export const getTeacherApplicationsForUser = ({ userId }) => ({
  ...GET_PARAMS,
  url: `${getBackendPath()}/api/users/${userId}/teacher_applications`,
});

export const getLatestTeacherApplicationForUser = ({
  userId,
  applicationExpirationRange = 1000 * 60 * 60 * 24 * 30 * 6, // ~6 months
}) => ({
  ...getTeacherApplicationsForUser({ userId }),
  data: {
    page_size: 1,
    order: CREATED_AT_DESCENDING.order,
    direction: CREATED_AT_DESCENDING.direction,
    not_submitted_before: (new Date(Date.now() - applicationExpirationRange)).toISOString(),
  },
});

export const submitTeacherApplication = (applicationId) => ({
  ...POST_PARAMS,
  url: `${getBackendPath()}/api/teacher_applications/${applicationId}/submit`,
});

export const oauthApplicationRoutes = entityRoutes('oauth/applications');

export const teacherRoutes = entityRoutes('teachers');
export const {
  get: getTeachers,
  getById: getTeacher,
} = teacherRoutes;

export const bibliographyRoutes = entityRoutes('bibliographies');

export const {
  search: searchLessonSentences,
} = entityRoutes('lessons/sentences');

export const dmmCallbackUrl = (state) => ({
  ...GET_PARAMS,
  url: `${getBackendPath()}/api/users/dmm_login_callback_url`,
  data: { state },
});

export const dmmCookieCheck = () => ({
  ...GET_PARAMS,
  url: `${getBackendPath()}/api/users/dmm_check`,
  withCredentials: true,
});

/*
 * This is a base64 encoding for the uknow top page. Because we
 * don't want to include their base64 encoding logic in our frontend,
 * the landing page will have to suffice.
 * There is a utility page to help with encoding and decoding these strings
 * at https://git.dmm.com/pages/matsushita-ken/dmm-url-encoder/
 * The exact url is https://eikaiwa.dmm.com/uknow/
 */
const UKNOW_CALLBACK = 'DRVESRUMTh1dDFpZWkEHGQUIWxhTVgkWFwlZCUVK';

export const dmmHeader = ({
  isDmmLoggedIn,
  isMobile,
}) => ({
  ...GET_PARAMS,
  url:  `https://navismithapis.com/v1/com/${isMobile ? 'sp' : 'pc'}/eikaiwa`,
  data: {
    login_callback_url: UKNOW_CALLBACK,
    footers: 'mainfooter,subfooter,copyright',
    headers: 'logo,global,login',
    logo: 'eikaiwa',
    is_login: isDmmLoggedIn,
  },
});
